import styled from "styled-components";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { postAnalytics } from "../../../../../PatientComponents/analytics";
import { buttonCSS, desktopCss } from "../general.style";
import doctor2Img from "../../../../assets/Mayo/Doctor4.jpg";
import Transition from "../Transition";
import { usePatientFacing } from "../../../../flow/patientFacing/hooks/usePatientFacing";
import GIPatientHeader from "../components/GIPatientHeader";
import TermsModal from "../components/TermsModal";

export default function GI2Landing() {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const { getEventDataLocal } = usePatientFacing(eventId);
  const event = getEventDataLocal();
  const caseId = event?.Case?.id;
  const [showTerms, setShowTerms] = useState(false);

  // Inspect case progress for both GI sendings
  const cpData = event?.Case?.CasesProgresses;
  const cpArray = Array.isArray(cpData) ? cpData : (cpData ? [cpData] : []);
  const giProgress = cpArray.find((cp) => cp?.facingType === "gi");
  const gi2Progress = cpArray.find((cp) => cp?.facingType === "gi2");
  const needsAvatar = !giProgress?.avatarSelection;
  const needsQuestionnaire = !giProgress?.answeredQuestionnaire && !gi2Progress?.answeredQuestionnaire;

  // Handle missing event data (expired or invalid link)
  if (!event) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        textAlign: 'center',
        background: '#f5f5f5'
      }}>
        <div style={{
          background: '#f8f9fa',
          borderRadius: '12px',
          padding: '40px',
          maxWidth: '500px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏰</div>
          <h1 style={{ color: '#dc3545', marginBottom: '16px', fontSize: '24px' }}>Link Expired</h1>
          <p style={{ color: '#6c757d', fontSize: '16px', lineHeight: '1.5' }}>
            This link has expired or is no longer valid.
          </p>
          <p style={{ color: '#6c757d', fontSize: '14px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #dee2e6' }}>
            If you have questions about your procedure, please contact your healthcare provider.
          </p>
        </div>
      </div>
    );
  }

  const startDestination = needsAvatar
    ? "../gi2/choose-avatar"
    : needsQuestionnaire
      ? "../gi2/Questionnaire/diabetesMedicines"
      : "../gi2/video1";

  const handleStartClick = () => {
    if (!needsAvatar && !needsQuestionnaire) {
      localStorage.setItem(`gi2-landing-visited-${eventId}`, "1");
    }
    postAnalytics(caseId, "start-button-clicked", undefined, "gi2");
  };

  // If the user already visited the landing page and everything is complete, jump straight to the video
  if (
    event &&
    localStorage.getItem(`gi2-landing-visited-${eventId}`) &&
    !needsAvatar &&
    !needsQuestionnaire
  ) {
    return <Navigate to="../gi2/video1" replace />;
  }

  const handleLegalLinkClick = () => {
    postAnalytics(caseId, "opened-tos", undefined, "gi2");
    setShowTerms(true);
  };

  return (
    <Transition>
      <StartContainer>
        <GIPatientHeader />
        <DoctorImage id="startPageNurse" src={doctor2Img} alt="doctor" />
        <ParagraphWrapper id="ParagraphWrapper">
          <Title id="HelloTitle">
            {t("GI2-Start-Title")}
          </Title>
          <Paragraph id="StartParagraph">
            {t("GI2-Start-Paragraph")}
          </Paragraph>
        </ParagraphWrapper>
        <BottomContentContainer>
          <StartButton
            id="StartButton"
            to={startDestination}
            onClick={handleStartClick}
          >
            {t("Start-Button")}
          </StartButton>
          <LegalTextWrapper>
            {t("Start-Legal-Explain")}
            &nbsp;
            <LegalLink
              id="LegalLink"
              onClick={handleLegalLinkClick}
            >
              {t("Start-Legal-Link")}
            </LegalLink>
          </LegalTextWrapper>
        </BottomContentContainer>
      </StartContainer>
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </Transition>
  );
}

const StartContainer = styled.div`
  ${desktopCss}
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-inline: 22px;
  padding-block-end: 1.2rem;
  padding-block-start: 4px;
  box-sizing: border-box;
  justify-content: space-between;
`;

const DoctorImage = styled.img`
  width: 280px;
  max-width: 100%;
  object-fit: contain;
`;

const Title = styled.h1`
  font-family: "Abraham", sans-serif;
  font-weight: 700;
  font-size: 24px;
  margin: 0;
  text-align: center;
`;

const BottomContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const Paragraph = styled.p`
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1.1875rem;
  margin-block: 0;
  text-align: center;
  margin-inline: 25px;
  line-height: 1.5;
`;

const StartButton = styled(Link)`
  ${buttonCSS}
  padding-block: 0.594rem;
  padding-inline: 4.563rem;
  box-shadow: 0px 3px 6px #00000029;
  margin-top: 20px;
`;

const LegalTextWrapper = styled.p`
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1rem;
  margin-top: 10px;
  margin-block: 0;
`;

const LegalLink = styled.button`
  background: none;
  border: none;
  color: #7A9DFD;
  cursor: pointer;
  font-family: "Abraham", sans-serif;
  font-weight: bold;
  font-size: 16px;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ParagraphWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.656rem;
  margin-top: 1.5rem;
`;
